import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  Output,
  OnChanges,
  SimpleChanges,
  ChangeDetectorRef
} from '@angular/core';

import { CommonModule } from '@angular/common';

import { MatTreeModule } from '@angular/material/tree';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';

import {
  CdkDrag,
  CdkDragDrop,
  CdkDropList,
  CdkDropListGroup,
  DragDropModule
} from '@angular/cdk/drag-drop';
import { createEmptyDigitalFolder, DigitalFolderPublic, DigitalItemPublic } from '../../../globalTypes/file.types';
import { MatDialog } from '@angular/material/dialog';
import { ManageFileDialogComponent, ManageFileDialogData, ManageFileDialogResult } from './manage-file.dialog';
import { lastValueFrom } from 'rxjs';
import { take } from 'rxjs/operators';
import { UploadService } from '../services/upload.service';
import { MatSnackBar } from '@angular/material/snack-bar';
import { UploadFile } from "./upload-file";


export interface FileTreeNode {
  id: string;
  name: string;
  type: 'folder' | 'file';
  extension?: string | null;

  /**
   * For folders this is the folder's parent folder.
   * For files this is the folder containing the file.
   */
  parentId: string | null;

  /**
   * Only folders have children.
   */
  children?: FileTreeNode[];

  /**
   * Reference to the original object.
   */
  data: DigitalFolderPublic | DigitalItemPublic;
}


@Component({
  selector: 'msh-file-tree',
  standalone: true,

  imports: [
    CommonModule,
    MatTreeModule,
    MatIconModule,
    MatButtonModule,
    UploadFile
],

  template: `
    <div
      class="w-full"
    >

      <!-- ROOT -->
      <div
        class="root-drop-zone"
      >

        <div class="tree">

          @for (node of rootNodes; track node.id) {
            <ng-container
              *ngTemplateOutlet="
                nodeTemplate;
                context: { $implicit: node, depth: 0 }
              "
            />
          }
        </div>
      </div>

    </div>
    <div class="flex justify-end w-full mt-4">
      <button mat-button (click)="addDigitalContent('folder')" color="accent">+ Add Folder </button>
    </div>

    <!-- NODE TEMPLATE -->
    <ng-template
      #nodeTemplate
      let-node
      let-depth="depth"
    >

      <div
        class="tree-node"
        [style.padding-left.px]="depth * 24"
      >

        <!-- NODE -->
        @if (node.type === 'folder') {
          <div
            class=" flex items-center gap-1  h-10 rounded hover:bg-copy/10 group "
          >

            <!-- EXPAND/COLLAPSE -->
              <button
                mat-icon-button
                class=""
                (click)="toggleFolder(node)"
                (mousedown)="$event.stopPropagation()"
              >
                <mat-icon class="!text-[20px]">
                  {{ isExpanded(node.id) ? 'expand_more' : 'chevron_right' }}
                </mat-icon>
              </button>

            <!-- ICON -->
            <mat-icon class="!text-[20px]">
                {{ isExpanded(node.id) ? 'folder_open' : 'folder' }}
            </mat-icon>

            <!-- NAME -->
            <span class="truncate select-none" >{{ node.name }}</span>
            <div class="grow">

              <div [class]="' ml-6 mr-2 h-8 rounded border-2 border-dashed border-transparent text-xs text-copy/40 flex items-center px-2 '
                + (draggingOverNode === node ? 'bg-copy/10 border-copy/40' : '')"
              >
                <msh-upload-file
                  class="block w-full"
                  [node]="node"
                  [maxFileSize]="'50MB'"
                  [existingFiles]="files"
                  [locationId]="locationId"
                  [listingId]="listingId"
                  (onFileLoading)="onImagesLoading($event)"
                  (onFilesUpdated)="onFileObjectsUpdated($event)"
                ></msh-upload-file>
              </div>
            </div>
            <mat-icon (click)="editDigitalContent(node)" class="!text-[20px] cursor-pointer opacity-20 hover:opacity-70">edit</mat-icon>
          </div>
        } @else{
          <div
            class=" flex items-center gap-1 h-10 rounded hover:bg-copy/10 group "
          >

            <!-- EXPAND/COLLAPSE -->
            <div class="w-8"></div>

            <!-- ICON -->
            <mat-icon class="!text-[20px]">description</mat-icon>

            <!-- NAME -->
            <span class="truncate select-none">{{ node.name }}@if(node.extension){.{{node.extension}}}</span>
            <div class="grow"></div>
            <mat-icon (click)="editDigitalContent(node)" class="!text-[20px] cursor-pointer opacity-20 hover:opacity-70">edit</mat-icon>
          </div>
        }

        <!-- CHILDREN DROP LIST -->
        @if (node.type === 'folder' && isExpanded(node.id)) {

          <div
            class="folder-children "
          >

            <!--  drop area -->
            
            @for (
              child of node.children;
              track child.id
            ) {

              <ng-container
                *ngTemplateOutlet="
                  nodeTemplate;
                  context: {
                    $implicit: child,
                    depth: depth + 1
                  }
                "
              />

            }
          </div>

        }

      </div>

    </ng-template>
  `,

  styles: [`
    :host {
      display: block;
      width: 100%;
    }

    .tree {
      min-height: 40px;
    }

    .root-drop-zone {
      min-height: 40px;
    }

    .tree-node {
      position: relative;
    }

    .folder-children {
      min-height: 2px;
    }

    .cdk-drag-preview {
      box-sizing: border-box;
    }

    .cdk-drag-placeholder {
      opacity: 0;
    }

    .cdk-drag-animating {
      transition: transform 150ms ease;
    }

    .folder-children.cdk-drop-list-dragging
      .tree-node:not(.cdk-drag-placeholder) {
      transition: transform 150ms ease;
    }
  `],

  changeDetection: ChangeDetectionStrategy.OnPush
})
export class FileTreeComponent implements OnChanges {

  /**
   * Folders supplied by the parent.
   *
   * A null parentFolderId means the folder is at the root.
   */
  @Input()
  folders: DigitalFolderPublic[] = [];

  /**
   * Files supplied by the parent.
   *
   * A null folderId means the file is at the root.
   */
  @Input()
  files: DigitalItemPublic[] = [];
  @Input()
  locationId: string = '';
  @Input()
  listingId: string = '';


  /**
   * Emitted whenever a file or folder is moved.
   *
   * parentId === null means the item was moved to root.
   */
  @Output() onFoldersChange = new EventEmitter<DigitalFolderPublic[]>();
  @Output() onFilesChange = new EventEmitter<DigitalItemPublic[]>();
  rootNodes: FileTreeNode[] = [];
  readonly rootDropListId = 'file-tree-root';
  expandedFolders = new Set<string>();
  rootParentId: string | null = null;
  draggingOverNode: FileTreeNode | null = null;
  removeFileIds: string[] = [];
  

  constructor(
    private dialog: MatDialog, 
    private cd: ChangeDetectorRef,
  ) {}

  ngOnChanges(changes: SimpleChanges): void {

    if (
      changes['folders'] ||
      changes['files']
    ) {
      this.buildTree();
    }

  }


  private buildTree(): void {

    const folders = new Map<string, FileTreeNode>();


    /*
     * Create all folders first.
     *
     * This allows folders to reference parents that
     * appear later in the input array.
     */
    for (const folder of this.folders) {
      folders.set(
        folder.id,
        {
          id: folder.id,
          name: folder.folderName,
          type: 'folder',
          parentId: folder.parentId,
          children: [],
          data: folder
        }
      );
      if (!folder.parentId) {
        this.rootParentId = folder.id;
        if(!this.expandedFolders.has(folder.id)) {
          this.expandedFolders.add(folder.id);
        }
      }
    }


    const root: FileTreeNode[] = [];




    /*
     * Add files.
     */
    for (const file of this.files) {

      const node: FileTreeNode = {
        id: file.id,
        name: file.title,
        type: 'file',
        parentId: file.parentId,
        data: file,
        extension: file.extension || null,
      };


      if (
        file.parentId &&
        folders.has(file.parentId)
      ) {

        folders
          .get(file.parentId)!
          .children!
          .push(node);
        this.expandedFolders.add(file.parentId);
      } else {

        root.push(node);

      }

    }

    /*
     * Connect folders to their parents.
     */
    for (const folder of folders.values()) {

      if (
        folder.parentId &&
        folders.has(folder.parentId)
      ) {

        folders
          .get(folder.parentId)!
          .children!
          .push(folder);

      } else {

        root.push(folder);

      }

    }

    this.rootNodes = root;

  }

  async addDigitalContent(type: 'folder' | 'file'): Promise<void> {
    const dialog = this.dialog.open(ManageFileDialogComponent, {
      data: {
        label: type === 'folder' ? 'Folder Name' : 'File Name',
        action: 'Add',
        info: [
          (type === 'folder' ? 'Enter a name for the new folder':'Enter a name for the new file'),
          (this.folders.length > 1 ? 'and select a parent folder.' : '')],
        // at this point, don't allow subfolders
        folders: this.folders.filter(folder => folder.id === this.rootParentId).map(folder => ({
          id: folder.id,
          folderName: folder.folderName,
          parentId: folder.parentId,
          contentLength: [this.folders, this.files].flat().filter(item => item.parentId === folder.id).length,
        })),
        selectedFolderId: this.rootParentId
      }
    });
    const result: ManageFileDialogResult = await lastValueFrom(dialog.afterClosed().pipe(take(1)));
    if (result) {
      if(type === 'folder'){
        const newFolder: DigitalFolderPublic = createEmptyDigitalFolder(result.inputValue, result.folderId || null); //, result.displayOrder || 0
        this.folders.push(newFolder);
        this.expandedFolders.add(newFolder.id);
      }
      this.buildTree();
      this.cd.markForCheck();
      if(type === 'file'){
        this.onFilesChange.emit(this.files);
      }else{
        this.onFoldersChange.emit(this.folders);
      }
    }
  }

  async editDigitalContent(node: FileTreeNode): Promise<void> {
    const folders = this.folders.filter(folder => folder.id !== node.id);
    const data: ManageFileDialogData = {
      label: node.type === 'folder' ? 'Folder' : 'File',
      action: 'Update',
      info: [
        (node.type === 'folder' ? 'Edit the folder name':'Edit the file name'),
        (folders.length > 1 ? 'and select a parent folder.' : '')],
      folders: folders.map(folder => ({
        id: folder.id,
        folderName: folder.folderName,
        parentId: folder.parentId,
        // displayOrder: folder.displayOrder,
        contentLength: [this.folders, this.files].flat().filter(item => item.parentId === folder.id).length,
      })),
      selectedFolderId: node.parentId || this.rootParentId || undefined,
      inputValue: node.name,
      extension: node.extension,
    };
    const dialog = this.dialog.open(ManageFileDialogComponent, {
      data: data
    });
    const result: ManageFileDialogResult = await lastValueFrom(dialog.afterClosed().pipe(take(1)));
    if (result) {
      // Handle the result of the edit dialog here.
      if(node.type === 'folder'){
        const folder = this.folders.find(f => f.id === node.id);
        if(folder){
          folder.folderName = result.inputValue;
          folder.parentId = result.folderId || null;
          // folder.displayOrder = result.displayOrder || 0;
        }
      }else{
        const file = this.files.find(f => f.id === node.id);
        if(file){
          file.title = result.inputValue;
          file.parentId = result.folderId || this.rootParentId || '';
        }
      }
      this.cd.markForCheck();
      setTimeout(() => {
      this.buildTree();
      this.cd.markForCheck();

      if(node.type === 'file'){
        this.onFilesChange.emit(this.files);
      }else{
        this.onFoldersChange.emit(this.folders);
      }
      }, 100);
    }
  }

  dragOver(event: any, node: FileTreeNode): void {
    // event.preventDefault();
    console.log('dragOver event:', event, 'node:', node);
  }

  onImagesLoading(fileNames: string[]){
    console.log({fileNames})
    // this.isLoading = fileNames.length > 0
  }


  
  
  async onFileObjectsUpdated(fileObjects: DigitalItemPublic[]){
    const currentIds = fileObjects.map(file => file.id);
    // test this against the tree data
    if(this.files.length > 0) {
      this.files.forEach(file => {
        if(!currentIds.includes(file.id)) {
          this.removeFileIds.push(file.id); // imageObjectIds
        }
      })
    }
    this.files = fileObjects;
    this.buildTree();
    this.cd.markForCheck();
    this.onFilesChange.emit(this.files);
  };

  toggleFolder(node: FileTreeNode): void {

    if (node.type !== 'folder') {
      return;
    }


    if (this.expandedFolders.has(node.id)) {

      this.expandedFolders.delete(node.id);

    } else {

      this.expandedFolders.add(node.id);

    }

  }


  isExpanded(id: string): boolean {
    return this.expandedFolders.has(id);
  }


  private findNode(
    id: string
  ): FileTreeNode | null {

    const search = (
      nodes: FileTreeNode[]
    ): FileTreeNode | null => {

      for (const node of nodes) {

        if (node.id === id) {
          return node;
        }


        if (node.type === 'folder') {

          const result = search(
            node.children ?? []
          );


          if (result) {
            return result;
          }

        }

      }


      return null;

    };


    return search(this.rootNodes);

  }

  canEnterFolder = (
    drag: CdkDrag<FileTreeNode>,
    drop: CdkDropList<FileTreeNode>
  ): boolean => {

    const draggedNode = drag.data;
    const destinationFolder = drop.data;

    // Files can be dropped into any folder.
    if (draggedNode.type === 'file') {
      return true;
    }

    // A folder cannot be dropped into itself.
    if (draggedNode.id === destinationFolder.id) {
      return false;
    }

    // A folder cannot be dropped into one of its descendants.
    return !this.isDescendant(
      destinationFolder.id,
      draggedNode.id
    );
  };

  private isDescendant(
    possibleDescendantId: string,
    folderId: string
  ): boolean {

    const folder = this.findNode(folderId);


    if (
      !folder ||
      folder.type !== 'folder'
    ) {

      return false;

    }


    const search = (
      children: FileTreeNode[]
    ): boolean => {

      for (const child of children) {

        if (
          child.id === possibleDescendantId
        ) {

          return true;

        }


        if (
          child.type === 'folder' &&
          search(child.children ?? [])
        ) {

          return true;

        }

      }


      return false;

    };


    return search(folder.children ?? []);

  }

}